import { MarkerClusterer, type Renderer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import {
  type FC,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { hasValue } from '@core/utils/equality';

import type { HasMap } from './SlabMap';

type ClustererContextValue = {
  clusterer: MarkerClusterer;
  map: google.maps.Map;
  /**
   * Coalesces many synchronous marker add/remove operations into a single
   * re-clustering on the next animation frame. Markers must add/remove with
   * `noDraw = true` and then call this instead of letting the clusterer redraw
   * per marker (which is O(N) per call and O(N²) for a full mount/unmount).
   */
  scheduleDraw: () => void;
};

const ClustererContext = createContext<ClustererContextValue | null>(null);

export const useClustererContext = (): ClustererContextValue | null => useContext(ClustererContext);

interface SlabMarkerClusterProps extends Partial<HasMap> {
  children: ReactNode;
  renderer?: Renderer;
  radius?: number;
  maxZoom?: number;
  onClusterClick?: (
    event: google.maps.MapMouseEvent,
    cluster: Parameters<NonNullable<MarkerClusterer['onClusterClick']>>[1],
    map: google.maps.Map,
  ) => void;
}

export const SlabMarkerCluster: FC<SlabMarkerClusterProps> = ({
  map,
  children,
  renderer,
  radius = 60,
  maxZoom = 16,
  onClusterClick,
}) => {
  const [clusterer, setClusterer] = useState<MarkerClusterer | null>(null);
  const drawFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!hasValue(map)) {
      return undefined;
    }

    const instance = new MarkerClusterer({
      map,
      renderer,
      algorithm: new SuperClusterAlgorithm({ radius, maxZoom }),
      onClusterClick,
    });

    setClusterer(instance);

    return (): void => {
      // Cancel any pending coalesced draw so we don't re-cluster a torn-down map.
      if (drawFrameRef.current !== null) {
        cancelAnimationFrame(drawFrameRef.current);
        drawFrameRef.current = null;
      }
      instance.clearMarkers();
      setClusterer(null);
    };
    // Renderer / handler changes are intentionally not re-applied here — the
    // clusterer captures them at construction time. Consumers must memoize
    // them upstream if they depend on render state.
  }, [map, radius, maxZoom]);

  const scheduleDraw = useCallback((): void => {
    if (drawFrameRef.current !== null) {
      return;
    }
    drawFrameRef.current = requestAnimationFrame(() => {
      drawFrameRef.current = null;
      clusterer?.render();
    });
  }, [clusterer]);

  const contextValue = useMemo(
    () => (hasValue(clusterer) && hasValue(map) ? { clusterer, map, scheduleDraw } : null),
    [clusterer, map, scheduleDraw],
  );

  if (!hasValue(contextValue)) {
    return null;
  }

  return <ClustererContext.Provider value={contextValue}>{children}</ClustererContext.Provider>;
};
